// ===================================================================
// PA GRANTS — FEMA Forms
// Project Worksheet generator, RPA forms, Certifications
// ===================================================================

import { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Paper, Text, Group, Stack, Badge, Title, Select, Button, Tabs, Table,
  Divider, Loader, Center, SimpleGrid,
} from '../../components/gab/GabComponents';
import { IconPrinter, IconFileDescription, IconCertificate, IconBuilding, IconPhoto, IconInfoCircle } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { fmtDate, fmtDateTime } from '../../lib/date-format';
import { useMyPAOrgIds } from '../../hooks/useMyPAOrgData';
import { useRoleStore } from '../../store/roleStore';
import { useAuthStore } from '../../store/authStore';
import {
  DAMAGE_CATEGORY_LABELS, type DamageCategory,
} from '../../types/pagrants';
import { primary } from '../../lib/colors';
import { formatPhone } from '../../lib/phoneFormat';

const APPLICANT_SLUGS = ['pa_applicant_mgr', 'pa_applicant_staff', 'pa_applicant_viewer'];

// V2 org mapper normalizes V2 field names to V1-style names used throughout this page
function normalizeOrg(org: Record<string, unknown> | null | undefined): Record<string, string> {
  if (!org) return {} as Record<string, string>;
  const o = org as Record<string, unknown>;
  return {
    ...(o as Record<string, string>),
    // V2 uses femaId; V1 used femaApplicantId
    femaApplicantId: String(o.femaId || o.femaApplicantId || ''),
    // V2 uses uei; V1 used ueiNumber
    ueiNumber: String(o.uei || o.ueiNumber || ''),
    // V2 uses address/city/state/zip for physical address
    physicalAddress: String(o.physicalAddress || o.address || ''),
    physicalCity: String(o.physicalCity || o.city || ''),
    physicalState: String(o.physicalState || o.state || ''),
    physicalZip: String(o.physicalZip || o.zip || ''),
    physicalCounty: String(o.physicalCounty || o.county || ''),
    // Contact fields — V2 may not have these; use notes as fallback
    primaryContact: String(o.primaryContact || ''),
    primaryTitle: String(o.primaryTitle || ''),
    primaryPhone: String(o.primaryPhone || o.phone || ''),
    altContact: String(o.altContact || ''),
    altTitle: String(o.altTitle || ''),
    altPhone: String(o.altPhone || ''),
    altEmail: String(o.altEmail || ''),
    einTaxId: String(o.einTaxId || ''),
    samRegistered: String(o.samRegistered || ''),
    samExpirationDate: String(o.samExpirationDate || ''),
    congressionalDistrict: String(o.congressionalDistrict || ''),
    pnpType: String(o.pnpType || ''),
    pnpTaxExemptVerified: String(o.pnpTaxExemptVerified || ''),
  } as Record<string, string>;
}

const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const num = (s: string) => parseFloat(s) || 0;

function printDiv(el: HTMLElement | null) {
  if (!el) return;
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><title>FEMA Form</title>
    <style>
      body { font-family: 'DM Sans', Arial, sans-serif; color: #333; max-width: 800px; margin: 0 auto; padding: 24px; }
      table { width: 100%; border-collapse: collapse; margin: 8px 0; }
      th, td { border: 1px solid #ccc; padding: 6px 8px; font-size: 12px; text-align: left; }
      th { background: #f0f0f0; font-weight: 600; }
      h1 { font-size: 18px; text-align: center; margin: 0; }
      h2 { font-size: 14px; background: var(--color-primary-800); color: #fff; padding: 4px 8px; margin: 16px 0 8px; }
      h3 { font-size: 13px; margin: 12px 0 4px; }
      .header { text-align: center; border-bottom: 3px solid var(--color-primary-800); padding-bottom: 8px; margin-bottom: 12px; }
      .field { display: flex; gap: 8px; margin: 2px 0; font-size: 12px; }
      .field-label { font-weight: 600; min-width: 180px; }
      .sig-line { border-bottom: 1px solid #333; min-width: 250px; display: inline-block; margin-top: 24px; }
      @media print { body { padding: 0; } }
    </style>
  </head><body>${el.innerHTML}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); w.close(); }, 250);
}

export default function FemaForms() {
  const {
    organizations, disasters, projects, projectCosts, compliance, insurance, documents,
    laborRecords, fetchLabor, equipRecords, fetchEquipment, materials, fetchMaterials,
    loadingProjects, fetchAll,
  } = usePAGrantsStore();
  const myOrgIds = useMyPAOrgIds();
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const isApplicantRole = APPLICANT_SLUGS.includes(effectiveRole?.roleSlug ?? '');

  // Scope data for applicant roles
  const scopedOrgs = useMemo(() =>
    isApplicantRole && myOrgIds.length > 0
      ? organizations.filter(o => myOrgIds.includes(String(o.Rid)))
      : organizations,
    [organizations, myOrgIds, isApplicantRole],
  );
  const scopedProjects = useMemo(() =>
    isApplicantRole && myOrgIds.length > 0
      ? projects.filter(p => myOrgIds.includes(String(p.organizationId)))
      : projects,
    [projects, myOrgIds, isApplicantRole],
  );

  const activeIncident = useAuthStore(s => s.activeIncident);
  const location = useLocation();

  const [selectedProject, setSelectedProject] = useState<string | null>(
    (location.state as { projectId?: string } | null)?.projectId ?? null
  );
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  const [selectedApplicant, setSelectedApplicant] = useState<string | null>(null);
  const [selectedDisaster, setSelectedDisaster] = useState<string | null>(null);

  const [packaging, setPackaging] = useState(false);
  const pwRef = useRef<HTMLDivElement>(null);
  const rpaRef = useRef<HTMLDivElement>(null);
  const certRef = useRef<HTMLDivElement>(null);
  const pnpRef = useRef<HTMLDivElement>(null);
  const photoRef = useRef<HTMLDivElement>(null);
  const laborRef = useRef<HTMLDivElement>(null);
  const equipRef = useRef<HTMLDivElement>(null);
  const materialsRef = useRef<HTMLDivElement>(null);
  const contractRef = useRef<HTMLDivElement>(null);
  const scqRef = useRef<HTMLDivElement>(null);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch force-account records when a project is selected
  useEffect(() => {
    if (!selectedProject) return;
    const id = Number(selectedProject);
    fetchLabor(id);
    fetchEquipment(id);
    fetchMaterials(id);
  }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select disaster linked to global active incident
  useEffect(() => {
    if (activeIncident && disasters.length > 0) {
      const linked = disasters.find(d => String(d.incidentId) === String(activeIncident.id));
      if (linked) setSelectedDisaster(String(linked.Rid));
    }
  }, [activeIncident, disasters]);

  const orgMap = useMemo(() => {
    const m = new Map<number, typeof organizations[number]>();
    organizations.forEach(o => m.set(o.Rid, o));
    return m;
  }, [organizations]);

  const project = useMemo(() => projects.find(p => String(p.Rid) === selectedProject) ?? null, [projects, selectedProject]);
  const orgRaw = useMemo(() => project ? orgMap.get(Number(project.organizationId)) ?? null : null, [project, orgMap]);
  const org = useMemo(() => normalizeOrg(orgRaw as Record<string, unknown> | null), [orgRaw]);
  const disaster = useMemo(() => {
    if (selectedDisaster) return disasters.find(d => String(d.Rid) === selectedDisaster) ?? null;
    return disasters[0] ?? null;
  }, [disasters, selectedDisaster]);
  const costs = useMemo(() => project ? projectCosts.filter(c => c.projectId === String(project.Rid)) : [], [project, projectCosts]);
  const projLabor = useMemo(() => project ? laborRecords.filter(r => String(r.projectId) === String(project.Rid)) : [], [project, laborRecords]);
  const projEquip = useMemo(() => project ? equipRecords.filter(e => String(e.projectId) === String(project.Rid)) : [], [project, equipRecords]);
  const projMaterials = useMemo(() => project ? materials.filter(m => String(m.projectId) === String(project.Rid)) : [], [project, materials]);
  const projContractCosts = useMemo(() => costs.filter(c => c.costType === 'contracts'), [costs]);
  const comp = useMemo(() => project ? compliance.find(c => c.projectId === String(project.Rid)) ?? null : null, [project, compliance]);
  const ins = useMemo(() => project ? insurance.filter(i => i.projectId === String(project.Rid)) : [], [project, insurance]);

  const selectedOrgObj = useMemo(() => {
    const raw = selectedOrg ? orgMap.get(Number(selectedOrg)) ?? null : null;
    return normalizeOrg(raw as Record<string, unknown> | null);
  }, [selectedOrg, orgMap]);

  const costTotal = costs.reduce((s, c) => s + num(c.totalCost), 0);

  const filteredProjectOptions = useMemo(() => {
    let list = scopedProjects;
    if (selectedDisaster) {
      list = list.filter(p => p.disasterId === selectedDisaster);
    }
    if (selectedApplicant) {
      list = list.filter(p => p.organizationId === selectedApplicant);
    }
    return list.map(p => {
      const pOrg = orgMap.get(Number(p.organizationId));
      return {
        value: String(p.Rid),
        label: `${p.projectNumber} — ${p.projectTitle}${pOrg ? ` (${pOrg.orgName})` : ''}`,
      };
    });
  }, [scopedProjects, selectedApplicant, selectedDisaster, orgMap]);

  if (loadingProjects && projects.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  const orgOptions = scopedOrgs.map(o => ({
    value: String(o.Rid),
    label: o.orgName,
  }));

  const disasterOptions = disasters.map(d => ({
    value: String(d.Rid),
    label: `DR-${d.disasterNumber} — ${d.disasterName || (d as any).declarationType || ''}`,
  }));

  function handleApplicantChange(val: string | null) {
    setSelectedApplicant(val);
    setSelectedProject(null);
  }

  function handleDisasterChange(val: string | null) {
    setSelectedDisaster(val);
    setSelectedProject(null);
  }

  // ── Shared field row component ─────────────────────────────
  const Field = ({ label, value }: { label: string; value: string }) => (
    <div className="field"><span className="field-label">{label}:</span><span>{value || '—'}</span></div>
  );

  if (isApplicantRole) {
    return (
      <Stack gap="lg">
        <Title order={3} c="black">FEMA Forms</Title>
        <Paper p="xl" withBorder ta="center">
          <Group justify="center" mb="sm">
            <IconInfoCircle size={24} color="var(--color-primary-700)" />
          </Group>
          <Text size="lg" fw={600} mb="xs">Form Generation Not Available</Text>
          <Text size="sm" c="dimmed">
            Contact your grantee to generate official FEMA forms. They will prepare and provide the completed
            forms for your projects.
          </Text>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Title order={3} c="black">FEMA Forms</Title>

      <Tabs defaultValue="pw">
        <Tabs.List style={{ flexWrap: 'wrap' }}>
          <Tabs.Tab value="pw" leftSection={<IconFileDescription size={14} />}>009-0-91 Project Worksheet</Tabs.Tab>
          <Tabs.Tab value="labor">009-0-91A Force Account Labor</Tabs.Tab>
          <Tabs.Tab value="equip">009-0-91B Force Account Equipment</Tabs.Tab>
          <Tabs.Tab value="materials">009-0-91C Materials Summary</Tabs.Tab>
          <Tabs.Tab value="contract">009-0-126 Contract Work</Tabs.Tab>
          <Tabs.Tab value="scq">009-0-120 Special Considerations</Tabs.Tab>
          <Tabs.Tab value="rpa">RPA (90-49)</Tabs.Tab>
          <Tabs.Tab value="cert" leftSection={<IconCertificate size={14} />}>Certifications</Tabs.Tab>
          <Tabs.Tab value="pnp" leftSection={<IconBuilding size={14} />}>PNP Questionnaire</Tabs.Tab>
          <Tabs.Tab value="photos" leftSection={<IconPhoto size={14} />}>Photo Sheet</Tabs.Tab>
          <Tabs.Tab value="package">FEMA Package</Tabs.Tab>
        </Tabs.List>

        {/* ═══ PROJECT WORKSHEET ════════════════════════════ */}
        <Tabs.Panel value="pw" pt="md">
          <Group mb="md" wrap="wrap">
            <Select size="sm" placeholder="Filter by disaster..." searchable clearable
              value={selectedDisaster} onChange={handleDisasterChange} data={disasterOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Filter by applicant..." searchable clearable
              value={selectedApplicant} onChange={handleApplicantChange} data={orgOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Select a project..." searchable clearable
              value={selectedProject} onChange={setSelectedProject} data={filteredProjectOptions}
              style={{ flex: 1, maxWidth: 500 }} />
            <Button size="sm" leftSection={<IconPrinter size={14} />} disabled={!project}
              onClick={() => printDiv(pwRef.current)}>Print / PDF</Button>
          </Group>

          {!project ? (
            <Paper p="xl" withBorder ta="center">
              <Text c="dimmed">Select a project above to generate its Project Worksheet.</Text>
            </Paper>
          ) : (
            <div ref={pwRef}>
            <Paper p="lg" radius="md" withBorder>
              {/* Header */}
              <div className="header">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Emergency Management Agency</Text>
                <Text size="lg" fw={700}>PROJECT WORKSHEET</Text>
                <Group justify="center" gap="lg" mt={4}>
                  <Text size="xs">Disaster: DR-{disaster?.disasterNumber || '—'}</Text>
                  <Text size="xs">PA ID: {org?.femaApplicantId || '—'}</Text>
                  <Text size="xs">Project: {project.projectNumber}</Text>
                  <Text size="xs">Category: {project.category} — {DAMAGE_CATEGORY_LABELS[project.category as DamageCategory] || ''}</Text>
                </Group>
              </div>

              {/* Section A: Applicant */}
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section A — Applicant Information
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Stack gap={2}>
                  <Field label="Applicant" value={org?.orgName || '—'} />
                  <Field label="Type" value={org?.orgType || '—'} />
                  <Field label="FIPS Code" value={org?.fipsCode || '—'} />
                  <Field label="FEMA Applicant ID" value={org?.femaApplicantId || '—'} />
                </Stack>
                <Stack gap={2}>
                  <Field label="UEI Number" value={org?.ueiNumber || '—'} />
                  <Field label="Congressional District" value={org?.congressionalDistrict || '—'} />
                  <Field label="Contact" value={org?.primaryContact || '—'} />
                  <Field label="Phone" value={org?.primaryPhone ? formatPhone(org.primaryPhone) : '—'} />
                </Stack>
              </SimpleGrid>

              {/* Section B: Damage */}
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section B — Damage Description
              </Text>
              <Text size="xs" mb="xs"><strong>Location:</strong> {project.projectAddress}, {project.projectCity}, {project.projectState} {project.projectZip}</Text>
              <Text size="xs" mb="xs"><strong>Work Type:</strong> {project.workType} | <strong>Size:</strong> {project.projectSize?.toUpperCase()}</Text>
              <Paper p="sm" radius="sm" style={{ background: '#f8f9fa' }}>
                <Text size="xs">{project.damageDescription || 'No damage description provided.'}</Text>
              </Paper>

              {/* Section C: Scope of Work */}
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section C — Scope of Work
              </Text>
              <Paper p="sm" radius="sm" style={{ background: '#f8f9fa' }}>
                <Text size="xs">{project.scopeOfWork || 'No scope of work provided.'}</Text>
              </Paper>

              {/* Section D: Cost Estimate */}
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section D — Cost Estimate
              </Text>
              {costs.length > 0 ? (
                <Table striped withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Description</Table.Th>
                      <Table.Th ta="right">Qty</Table.Th>
                      <Table.Th>Unit</Table.Th>
                      <Table.Th ta="right">Unit Cost</Table.Th>
                      <Table.Th ta="right">Total</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {costs.map(c => (
                      <Table.Tr key={c.Rid}>
                        <Table.Td><Text size="xs">{c.description}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs">{c.quantity}</Text></Table.Td>
                        <Table.Td><Text size="xs">{c.unit}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs">{fmt(num(c.unitCost))}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs" fw={600}>{fmt(num(c.totalCost))}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                    <Table.Tr style={{ background: '#e7f5ff' }}>
                      <Table.Td colSpan={4}><Text size="xs" fw={700}>TOTAL</Text></Table.Td>
                      <Table.Td ta="right"><Text size="xs" fw={700}>{fmt(costTotal)}</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              ) : (
                <Text size="xs" c="dimmed">No cost line items recorded.</Text>
              )}

              {/* Section E: Cost Summary */}
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section E — Project Cost Summary
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Stack gap={2}>
                  <Field label="Estimated Cost" value={fmt(num(project.estimatedCost))} />
                  <Field label="Insurance Deduction" value={fmt(num(project.insuranceDeduction))} />
                  <Field label="Net Eligible Cost" value={fmt(num(project.netEligibleCost))} />
                </Stack>
                <Stack gap={2}>
                  <Field label="Federal Share (75%)" value={fmt(num(project.federalShareAmount))} />
                  <Field label="Applicant Share (25%)" value={fmt(num(project.applicantShareAmount))} />
                  <Field label="Obligated Amount" value={fmt(num(project.obligatedAmount))} />
                </Stack>
              </SimpleGrid>

              {/* Section F: Hazard Mitigation (if applicable) */}
              {project.hazardMitigationIncluded === 'Yes' && (
                <>
                  <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                    Section F — Hazard Mitigation Proposal (Section 406)
                  </Text>
                  <Field label="Mitigation Cost" value={fmt(num(project.hazardMitigationCost))} />
                  <Paper p="sm" radius="sm" style={{ background: '#f8f9fa' }} mt={4}>
                    <Text size="xs">{project.hazardMitigationDescription || 'No description.'}</Text>
                  </Paper>
                </>
              )}

              {/* Compliance summary */}
              {comp && (
                <>
                  <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                    Environmental / Historic Compliance
                  </Text>
                  <SimpleGrid cols={{ base: 1, sm: 2 }}>
                    <Stack gap={2}>
                      <Field label="NEPA Level" value={comp.nepaLevel} />
                      <Field label="NEPA Status" value={comp.nepaStatus} />
                      <Field label="Section 106 Required" value={comp.section106Required} />
                      <Field label="Section 106 Status" value={comp.section106Status} />
                    </Stack>
                    <Stack gap={2}>
                      <Field label="In Floodplain" value={comp.inFloodplain} />
                      <Field label="Flood Zone" value={comp.floodZone} />
                      <Field label="HAZMAT Present" value={comp.hazmatPresent} />
                      <Field label="Overall Status" value={comp.overallComplianceStatus} />
                    </Stack>
                  </SimpleGrid>
                </>
              )}

              {/* Insurance summary */}
              {ins.length > 0 && (
                <>
                  <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                    Insurance Information
                  </Text>
                  {ins.map(i => (
                    <SimpleGrid cols={{ base: 1, sm: 2 }} key={i.Rid} mb="xs">
                      <Stack gap={2}>
                        <Field label="Carrier" value={i.carrier} />
                        <Field label="Policy #" value={i.policyNumber} />
                        <Field label="Coverage" value={fmt(num(i.coverageAmount))} />
                      </Stack>
                      <Stack gap={2}>
                        <Field label="Claim Filed" value={i.claimFiled} />
                        <Field label="FEMA Deduction" value={fmt(num(i.femaDeductionAmount))} />
                        <Field label="O&M Required" value={i.obtainMaintainRequired} />
                      </Stack>
                    </SimpleGrid>
                  ))}
                </>
              )}
            </Paper>
            </div>
          )}
        </Tabs.Panel>

        {/* ═══ 009-0-91A FORCE ACCOUNT LABOR ════════════════ */}
        <Tabs.Panel value="labor" pt="md">
          <Group mb="md" wrap="wrap">
            <Select size="sm" placeholder="Filter by disaster..." searchable clearable value={selectedDisaster} onChange={handleDisasterChange} data={disasterOptions} style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Filter by applicant..." searchable clearable value={selectedApplicant} onChange={handleApplicantChange} data={orgOptions} style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Select a project..." searchable clearable value={selectedProject} onChange={setSelectedProject} data={filteredProjectOptions} style={{ flex: 1, maxWidth: 500 }} />
            <Button size="sm" leftSection={<IconPrinter size={14} />} disabled={!project} onClick={() => printDiv(laborRef.current)}>Print / PDF</Button>
          </Group>
          {!project ? (
            <Paper p="xl" withBorder ta="center"><Text c="dimmed">Select a project to generate the Force Account Labor Summary.</Text></Paper>
          ) : (
            <div ref={laborRef}>
            <Paper p="lg" radius="md" withBorder>
              <div className="header">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Emergency Management Agency</Text>
                <Text size="lg" fw={700}>FORCE ACCOUNT LABOR SUMMARY</Text>
                <Text size="xs" c="dimmed">FEMA Form 009-0-91A</Text>
                <Group justify="center" gap="lg" mt={4}>
                  <Text size="xs">Disaster: DR-{disaster?.disasterNumber || '—'}</Text>
                  <Text size="xs">PA ID: {org?.femaApplicantId || '—'}</Text>
                  <Text size="xs">Project: {project.projectNumber}</Text>
                </Group>
              </div>
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Applicant &amp; Project Information
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Stack gap={2}>
                  <Field label="Applicant" value={org?.orgName || '—'} />
                  <Field label="Project Title" value={project.projectTitle} />
                </Stack>
                <Stack gap={2}>
                  <Field label="Project Number" value={project.projectNumber} />
                  <Field label="Category" value={`${project.category} — ${DAMAGE_CATEGORY_LABELS[project.category as DamageCategory] || ''}`} />
                </Stack>
              </SimpleGrid>
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Labor Records
              </Text>
              {projLabor.length === 0 ? (
                <Text size="xs" c="dimmed">No force account labor records for this project.</Text>
              ) : (
                <Table striped withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Employee Name</Table.Th>
                      <Table.Th>Activity Description</Table.Th>
                      <Table.Th>Work Date</Table.Th>
                      <Table.Th ta="right">Reg Hours</Table.Th>
                      <Table.Th ta="right">OT Hours</Table.Th>
                      <Table.Th ta="right">Total Cost</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {projLabor.map(r => (
                      <Table.Tr key={r.id}>
                        <Table.Td><Text size="xs">{r.employeeName || '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{r.activityDescription || '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{fmtDate(r.workDate)}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs">{r.hoursRegular}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs">{r.hoursOvertime}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs" fw={600}>{fmt(r.totalCost)}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                    <Table.Tr style={{ background: '#e7f5ff' }}>
                      <Table.Td colSpan={3}><Text size="xs" fw={700}>TOTALS</Text></Table.Td>
                      <Table.Td ta="right"><Text size="xs" fw={700}>{projLabor.reduce((s, r) => s + r.hoursRegular, 0)}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="xs" fw={700}>{projLabor.reduce((s, r) => s + r.hoursOvertime, 0)}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="xs" fw={700}>{fmt(projLabor.reduce((s, r) => s + r.totalCost, 0))}</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              )}
              <Divider my="lg" />
              <Text size="xs" fw={600}>Certification:</Text>
              <Text size="xs" c="dimmed" mt="xs">I certify that the above information is correct and all hours were worked directly in connection with the declared disaster.</Text>
              <Group mt="xl" gap="xl">
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 300, height: 30 }} />
                  <Text size="xs" c="dimmed">Signature of Authorized Representative</Text>
                </Stack>
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 150, height: 30 }} />
                  <Text size="xs" c="dimmed">Date</Text>
                </Stack>
              </Group>
            </Paper>
            </div>
          )}
        </Tabs.Panel>

        {/* ═══ 009-0-91B FORCE ACCOUNT EQUIPMENT ════════════ */}
        <Tabs.Panel value="equip" pt="md">
          <Group mb="md" wrap="wrap">
            <Select size="sm" placeholder="Filter by disaster..." searchable clearable value={selectedDisaster} onChange={handleDisasterChange} data={disasterOptions} style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Filter by applicant..." searchable clearable value={selectedApplicant} onChange={handleApplicantChange} data={orgOptions} style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Select a project..." searchable clearable value={selectedProject} onChange={setSelectedProject} data={filteredProjectOptions} style={{ flex: 1, maxWidth: 500 }} />
            <Button size="sm" leftSection={<IconPrinter size={14} />} disabled={!project} onClick={() => printDiv(equipRef.current)}>Print / PDF</Button>
          </Group>
          {!project ? (
            <Paper p="xl" withBorder ta="center"><Text c="dimmed">Select a project to generate the Force Account Equipment Summary.</Text></Paper>
          ) : (
            <div ref={equipRef}>
            <Paper p="lg" radius="md" withBorder>
              <div className="header">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Emergency Management Agency</Text>
                <Text size="lg" fw={700}>FORCE ACCOUNT EQUIPMENT SUMMARY</Text>
                <Text size="xs" c="dimmed">FEMA Form 009-0-91B</Text>
                <Group justify="center" gap="lg" mt={4}>
                  <Text size="xs">Disaster: DR-{disaster?.disasterNumber || '—'}</Text>
                  <Text size="xs">PA ID: {org?.femaApplicantId || '—'}</Text>
                  <Text size="xs">Project: {project.projectNumber}</Text>
                </Group>
              </div>
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Applicant &amp; Project Information
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Stack gap={2}>
                  <Field label="Applicant" value={org?.orgName || '—'} />
                  <Field label="Project Title" value={project.projectTitle} />
                </Stack>
                <Stack gap={2}>
                  <Field label="Project Number" value={project.projectNumber} />
                  <Field label="Category" value={`${project.category} — ${DAMAGE_CATEGORY_LABELS[project.category as DamageCategory] || ''}`} />
                </Stack>
              </SimpleGrid>
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Equipment Records
              </Text>
              {projEquip.length === 0 ? (
                <Text size="xs" c="dimmed">No force account equipment records for this project.</Text>
              ) : (
                <Table striped withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Equipment</Table.Th>
                      <Table.Th>Type / Size</Table.Th>
                      <Table.Th>Operator</Table.Th>
                      <Table.Th>Work Date</Table.Th>
                      <Table.Th ta="right">Hours Used</Table.Th>
                      <Table.Th ta="right">Rate/Hr</Table.Th>
                      <Table.Th ta="right">Total Cost</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {projEquip.map(e => (
                      <Table.Tr key={e.id}>
                        <Table.Td><Text size="xs">{e.equipmentName || '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{[e.equipmentType, e.size].filter(Boolean).join(' / ') || '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{e.operator || '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{fmtDate(e.workDate)}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs">{e.hoursUsed}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs">{fmt(e.ratePerHour)}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs" fw={600}>{fmt(e.totalCost)}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                    <Table.Tr style={{ background: '#e7f5ff' }}>
                      <Table.Td colSpan={4}><Text size="xs" fw={700}>TOTALS</Text></Table.Td>
                      <Table.Td ta="right"><Text size="xs" fw={700}>{projEquip.reduce((s, e) => s + e.hoursUsed, 0)}</Text></Table.Td>
                      <Table.Td ta="right" />
                      <Table.Td ta="right"><Text size="xs" fw={700}>{fmt(projEquip.reduce((s, e) => s + e.totalCost, 0))}</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              )}
              <Divider my="lg" />
              <Text size="xs" fw={600}>Certification:</Text>
              <Text size="xs" c="dimmed" mt="xs">I certify that the above equipment was used directly in connection with the declared disaster and that the rates shown are the applicant's actual equipment rates.</Text>
              <Group mt="xl" gap="xl">
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 300, height: 30 }} />
                  <Text size="xs" c="dimmed">Signature of Authorized Representative</Text>
                </Stack>
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 150, height: 30 }} />
                  <Text size="xs" c="dimmed">Date</Text>
                </Stack>
              </Group>
            </Paper>
            </div>
          )}
        </Tabs.Panel>

        {/* ═══ 009-0-91C MATERIALS SUMMARY ══════════════════ */}
        <Tabs.Panel value="materials" pt="md">
          <Group mb="md" wrap="wrap">
            <Select size="sm" placeholder="Filter by disaster..." searchable clearable value={selectedDisaster} onChange={handleDisasterChange} data={disasterOptions} style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Filter by applicant..." searchable clearable value={selectedApplicant} onChange={handleApplicantChange} data={orgOptions} style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Select a project..." searchable clearable value={selectedProject} onChange={setSelectedProject} data={filteredProjectOptions} style={{ flex: 1, maxWidth: 500 }} />
            <Button size="sm" leftSection={<IconPrinter size={14} />} disabled={!project} onClick={() => printDiv(materialsRef.current)}>Print / PDF</Button>
          </Group>
          {!project ? (
            <Paper p="xl" withBorder ta="center"><Text c="dimmed">Select a project to generate the Materials Summary.</Text></Paper>
          ) : (
            <div ref={materialsRef}>
            <Paper p="lg" radius="md" withBorder>
              <div className="header">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Emergency Management Agency</Text>
                <Text size="lg" fw={700}>MATERIALS SUMMARY</Text>
                <Text size="xs" c="dimmed">FEMA Form 009-0-91C</Text>
                <Group justify="center" gap="lg" mt={4}>
                  <Text size="xs">Disaster: DR-{disaster?.disasterNumber || '—'}</Text>
                  <Text size="xs">PA ID: {org?.femaApplicantId || '—'}</Text>
                  <Text size="xs">Project: {project.projectNumber}</Text>
                </Group>
              </div>
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Applicant &amp; Project Information
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Stack gap={2}>
                  <Field label="Applicant" value={org?.orgName || '—'} />
                  <Field label="Project Title" value={project.projectTitle} />
                </Stack>
                <Stack gap={2}>
                  <Field label="Project Number" value={project.projectNumber} />
                  <Field label="Category" value={`${project.category} — ${DAMAGE_CATEGORY_LABELS[project.category as DamageCategory] || ''}`} />
                </Stack>
              </SimpleGrid>
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Materials &amp; Supplies
              </Text>
              {projMaterials.length === 0 ? (
                <Text size="xs" c="dimmed">No materials records for this project.</Text>
              ) : (
                <Table striped withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Vendor</Table.Th>
                      <Table.Th>Invoice #</Table.Th>
                      <Table.Th>Purchase Date</Table.Th>
                      <Table.Th ta="right">Qty</Table.Th>
                      <Table.Th ta="right">Unit Cost</Table.Th>
                      <Table.Th ta="right">Total</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {projMaterials.map(m => (
                      <Table.Tr key={m.id}>
                        <Table.Td><Text size="xs">{m.description || '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{m.vendor || '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{m.invoiceNumber || '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{fmtDate(m.purchaseDate)}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs">{m.quantity}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs">{fmt(m.unitCost)}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs" fw={600}>{fmt(m.totalCost)}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                    <Table.Tr style={{ background: '#e7f5ff' }}>
                      <Table.Td colSpan={6}><Text size="xs" fw={700}>TOTAL</Text></Table.Td>
                      <Table.Td ta="right"><Text size="xs" fw={700}>{fmt(projMaterials.reduce((s, m) => s + m.totalCost, 0))}</Text></Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              )}
              <Divider my="lg" />
              <Text size="xs" fw={600}>Certification:</Text>
              <Text size="xs" c="dimmed" mt="xs">I certify that the materials listed above were purchased and used solely in connection with the declared disaster and eligible work described in the Project Worksheet.</Text>
              <Group mt="xl" gap="xl">
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 300, height: 30 }} />
                  <Text size="xs" c="dimmed">Signature of Authorized Representative</Text>
                </Stack>
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 150, height: 30 }} />
                  <Text size="xs" c="dimmed">Date</Text>
                </Stack>
              </Group>
            </Paper>
            </div>
          )}
        </Tabs.Panel>

        {/* ═══ 009-0-126 CONTRACT WORK SUMMARY ═════════════ */}
        <Tabs.Panel value="contract" pt="md">
          <Group mb="md" wrap="wrap">
            <Select size="sm" placeholder="Filter by disaster..." searchable clearable value={selectedDisaster} onChange={handleDisasterChange} data={disasterOptions} style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Filter by applicant..." searchable clearable value={selectedApplicant} onChange={handleApplicantChange} data={orgOptions} style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Select a project..." searchable clearable value={selectedProject} onChange={setSelectedProject} data={filteredProjectOptions} style={{ flex: 1, maxWidth: 500 }} />
            <Button size="sm" leftSection={<IconPrinter size={14} />} disabled={!project} onClick={() => printDiv(contractRef.current)}>Print / PDF</Button>
          </Group>
          {!project ? (
            <Paper p="xl" withBorder ta="center"><Text c="dimmed">Select a project to generate the Contract Work Summary.</Text></Paper>
          ) : (
            <div ref={contractRef}>
            <Paper p="lg" radius="md" withBorder>
              <div className="header">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Emergency Management Agency</Text>
                <Text size="lg" fw={700}>CONTRACT WORK SUMMARY</Text>
                <Text size="xs" c="dimmed">FEMA Form 009-0-126</Text>
                <Group justify="center" gap="lg" mt={4}>
                  <Text size="xs">Disaster: DR-{disaster?.disasterNumber || '—'}</Text>
                  <Text size="xs">PA ID: {org?.femaApplicantId || '—'}</Text>
                  <Text size="xs">Project: {project.projectNumber}</Text>
                </Group>
              </div>
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Project Information
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Stack gap={2}>
                  <Field label="Applicant" value={org?.orgName || '—'} />
                  <Field label="Project Title" value={project.projectTitle} />
                  <Field label="Location" value={`${project.projectAddress || ''}, ${project.projectCity || ''}, ${project.projectState || ''}`} />
                </Stack>
                <Stack gap={2}>
                  <Field label="Project Number" value={project.projectNumber} />
                  <Field label="Category" value={`${project.category} — ${DAMAGE_CATEGORY_LABELS[project.category as DamageCategory] || ''}`} />
                </Stack>
              </SimpleGrid>
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Scope of Work
              </Text>
              <Paper p="sm" radius="sm" style={{ background: '#f8f9fa' }}>
                <Text size="xs">{project.scopeOfWork || project.damageDescription || 'See Project Worksheet.'}</Text>
              </Paper>
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Contract Cost Items
              </Text>
              {projContractCosts.length === 0 ? (
                <Text size="xs" c="dimmed">No contract cost items recorded for this project.</Text>
              ) : (
                <Table striped withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Contractor / Source</Table.Th>
                      <Table.Th>Invoice / Description</Table.Th>
                      <Table.Th ta="right">Amount</Table.Th>
                      <Table.Th>Comments</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {projContractCosts.map(c => (
                      <Table.Tr key={c.Rid}>
                        <Table.Td><Text size="xs">{c.sourceReference || c.description || '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{c.description || '—'}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs" fw={600}>{fmt(num(c.totalCost))}</Text></Table.Td>
                        <Table.Td><Text size="xs">{c.notes || ''}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                    <Table.Tr style={{ background: '#e7f5ff' }}>
                      <Table.Td colSpan={2}><Text size="xs" fw={700}>GRAND TOTAL</Text></Table.Td>
                      <Table.Td ta="right"><Text size="xs" fw={700}>{fmt(projContractCosts.reduce((s, c) => s + num(c.totalCost), 0))}</Text></Table.Td>
                      <Table.Td />
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              )}
              <Divider my="lg" />
              <Text size="xs" c="dimmed" fs="italic" mb="lg">
                I certify that to the best of my knowledge and belief the above information is correct and that all expenditures were made in accordance with applicable laws and regulations and the terms and conditions of the FEMA grant.
              </Text>
              <Group gap="xl">
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 300, height: 30 }} />
                  <Text size="xs" c="dimmed">Authorized Representative Signature</Text>
                </Stack>
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 150, height: 30 }} />
                  <Text size="xs" c="dimmed">Date</Text>
                </Stack>
              </Group>
            </Paper>
            </div>
          )}
        </Tabs.Panel>

        {/* ═══ 009-0-120 SPECIAL CONSIDERATIONS ════════════ */}
        <Tabs.Panel value="scq" pt="md">
          <Group mb="md" wrap="wrap">
            <Select size="sm" placeholder="Filter by disaster..." searchable clearable value={selectedDisaster} onChange={handleDisasterChange} data={disasterOptions} style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Filter by applicant..." searchable clearable value={selectedApplicant} onChange={handleApplicantChange} data={orgOptions} style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Select a project..." searchable clearable value={selectedProject} onChange={setSelectedProject} data={filteredProjectOptions} style={{ flex: 1, maxWidth: 500 }} />
            <Button size="sm" leftSection={<IconPrinter size={14} />} disabled={!project} onClick={() => printDiv(scqRef.current)}>Print / PDF</Button>
          </Group>
          {!project ? (
            <Paper p="xl" withBorder ta="center"><Text c="dimmed">Select a project to generate the Special Consideration Questions form.</Text></Paper>
          ) : !comp ? (
            <Paper p="xl" withBorder ta="center">
              <Text c="dimmed">No compliance record for this project.</Text>
              <Text size="xs" c="dimmed" mt="xs">Add a compliance record from the project's Compliance tab first.</Text>
            </Paper>
          ) : (
            <div ref={scqRef}>
            <Paper p="lg" radius="md" withBorder>
              <div className="header">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Emergency Management Agency</Text>
                <Text size="lg" fw={700}>SPECIAL CONSIDERATION QUESTIONS</Text>
                <Text size="xs" c="dimmed">FEMA Form 009-0-120</Text>
                <Group justify="center" gap="lg" mt={4}>
                  <Text size="xs">Disaster: DR-{disaster?.disasterNumber || '—'}</Text>
                  <Text size="xs">PA ID: {org?.femaApplicantId || '—'}</Text>
                  <Text size="xs">Project: {project.projectNumber}</Text>
                </Group>
              </div>
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Applicant &amp; Project Information
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Stack gap={2}>
                  <Field label="Applicant" value={org?.orgName || '—'} />
                  <Field label="Project Number" value={project.projectNumber} />
                  <Field label="Project Title" value={project.projectTitle} />
                </Stack>
                <Stack gap={2}>
                  <Field label="Location" value={`${project.projectAddress || ''}, ${project.projectCity || ''}, ${project.projectState || ''}`} />
                  <Field label="Category" value={`${project.category} — ${DAMAGE_CATEGORY_LABELS[project.category as DamageCategory] || ''}`} />
                </Stack>
              </SimpleGrid>
              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Special Consideration Questions
              </Text>
              <Table striped withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: '60%' }}>Question</Table.Th>
                    <Table.Th style={{ width: '10%' }}>Answer</Table.Th>
                    <Table.Th>Comments / Details</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {([
                    ['1. Does the damaged facility or item of work have insurance and/or is it an insurable risk?', comp.floodInsuranceRequired, ''],
                    ['2. Is the facility located in a floodplain, coastal high-hazard area, or does it impact a wetland?', comp.inFloodplain, comp.floodZone ? `Flood Zone: ${comp.floodZone}` : ''],
                    ['3. Is the facility located within or adjacent to a Coastal Barrier Resource System unit?', comp.coastalBarrierArea, comp.coastalZone ? `Coastal Zone: ${comp.coastalZone}` : ''],
                    ['4. Will proposed repairs change the pre-disaster condition (footprint, material, location, use)?', comp.eo11988Compliance ? 'Yes' : 'No', comp.eo11988Compliance ? `EO 11988: ${comp.eo11988Compliance}` : ''],
                    ['5. Does the applicant have or want a hazard mitigation proposal?', project.hazardMitigationIncluded || '—', project.hazardMitigationDescription || ''],
                    ['6. Is the facility on the National Register, state historic listing, or older than 50 years?', comp.historicProperty, comp.section106Required ? `Section 106: ${comp.section106Required}` : ''],
                    ['7. Is there contamination or hazardous material at or near the site?', comp.hazmatPresent, comp.hazmatType ? `Type: ${comp.hazmatType}` : ''],
                    ['8. Are there environmental or ecological concerns (endangered species, wetlands, wild/scenic rivers)?', comp.esaConsultationRequired, comp.nepaRequired ? `NEPA: ${comp.nepaRequired}${comp.nepaLevel ? ` (${comp.nepaLevel})` : ''}` : ''],
                  ] as [string, string, string][]).map(([q, a, comment]) => (
                    <Table.Tr key={q.slice(0, 20)}>
                      <Table.Td><Text size="xs">{q}</Text></Table.Td>
                      <Table.Td ta="center">
                        <Badge size="xs" color={a === 'Yes' ? '#F86A0B' : a === 'No' ? 'var(--color-success)' : '#939598'} variant="light">
                          {a || '—'}
                        </Badge>
                      </Table.Td>
                      <Table.Td><Text size="xs">{comment || ''}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              {comp.complianceNotes && (
                <>
                  <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>Additional Notes</Text>
                  <Paper p="sm" radius="sm" style={{ background: '#f8f9fa' }}>
                    <Text size="xs">{comp.complianceNotes}</Text>
                  </Paper>
                </>
              )}
              <Divider my="lg" />
              <Group gap="xl">
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 300, height: 30 }} />
                  <Text size="xs" c="dimmed">Signature of Authorized Representative</Text>
                </Stack>
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 150, height: 30 }} />
                  <Text size="xs" c="dimmed">Date</Text>
                </Stack>
              </Group>
            </Paper>
            </div>
          )}
        </Tabs.Panel>

        {/* ═══ REQUEST FOR PUBLIC ASSISTANCE ════════════════ */}
        <Tabs.Panel value="rpa" pt="md">
          <Group mb="md">
            <Select size="sm" placeholder="Select an applicant..." searchable clearable
              value={selectedOrg} onChange={setSelectedOrg} data={orgOptions}
              style={{ flex: 1, maxWidth: 400 }} />
            <Button size="sm" leftSection={<IconPrinter size={14} />} disabled={!selectedOrg}
              onClick={() => printDiv(rpaRef.current)}>Print / PDF</Button>
          </Group>

          {!selectedOrg ? (
            <Paper p="xl" withBorder ta="center">
              <Text c="dimmed">Select an applicant to generate the Request for Public Assistance form.</Text>
            </Paper>
          ) : (
            <div ref={rpaRef}>
            <Paper p="lg" radius="md" withBorder>
              <div className="header">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Emergency Management Agency</Text>
                <Text size="lg" fw={700}>REQUEST FOR PUBLIC ASSISTANCE</Text>
                <Text size="xs" c="dimmed">FEMA Form 90-49</Text>
              </div>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Disaster Information
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Stack gap={2}>
                  <Field label="Disaster Number" value={`DR-${disaster?.disasterNumber || '—'}`} />
                  <Field label="Disaster Name" value={disaster?.disasterName || '—'} />
                  <Field label="Declaration Date" value={fmtDate(disaster?.declarationDate)} />
                </Stack>
                <Stack gap={2}>
                  <Field label="State" value={disaster?.state || '—'} />
                  <Field label="Designated Counties" value={disaster?.designatedCounties || '—'} />
                  <Field label="Incident Period" value={`${fmtDate(disaster?.incidentPeriodStart)} to ${fmtDate(disaster?.incidentPeriodEnd)}`} />
                </Stack>
              </SimpleGrid>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Applicant Information
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Stack gap={2}>
                  <Field label="Applicant Name" value={selectedOrgObj.orgName} />
                  <Field label="Type" value={selectedOrgObj.orgType} />
                  <Field label="Role" value={selectedOrgObj.orgRole} />
                  <Field label="FIPS Code" value={selectedOrgObj.fipsCode} />
                  <Field label="FEMA Applicant ID" value={selectedOrgObj.femaApplicantId} />
                </Stack>
                <Stack gap={2}>
                  <Field label="UEI Number" value={selectedOrgObj.ueiNumber} />
                  <Field label="EIN/Tax ID" value={selectedOrgObj.einTaxId} />
                  <Field label="SAM Registered" value={selectedOrgObj.samRegistered} />
                  <Field label="SAM Expiration" value={fmtDate(selectedOrgObj.samExpirationDate)} />
                  <Field label="Congressional District" value={selectedOrgObj.congressionalDistrict} />
                </Stack>
              </SimpleGrid>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Address
              </Text>
              <Text size="xs">
                {selectedOrgObj.physicalAddress}, {selectedOrgObj.physicalCity}, {selectedOrgObj.physicalState} {selectedOrgObj.physicalZip}
              </Text>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Authorized Representative
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Stack gap={2}>
                  <Field label="Name" value={selectedOrgObj.primaryContact} />
                  <Field label="Title" value={selectedOrgObj.primaryTitle} />
                </Stack>
                <Stack gap={2}>
                  <Field label="Email" value={selectedOrgObj.primaryEmail} />
                  <Field label="Phone" value={selectedOrgObj.primaryPhone} />
                </Stack>
              </SimpleGrid>

              {selectedOrgObj.orgType === 'nonprofit' && (
                <>
                  <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                    Private Nonprofit Information
                  </Text>
                  <SimpleGrid cols={{ base: 1, sm: 2 }}>
                    <Stack gap={2}>
                      <Field label="PNP Type" value={selectedOrgObj.pnpType} />
                      <Field label="Tax Exempt Verified" value={selectedOrgObj.pnpTaxExemptVerified} />
                    </Stack>
                    <Stack gap={2}>
                      <Field label="Open to Public" value={selectedOrgObj.pnpOpenToPublic} />
                      <Field label="Critical Facility" value={selectedOrgObj.pnpCriticalFacility} />
                    </Stack>
                  </SimpleGrid>
                  <Field label="Services" value={selectedOrgObj.pnpServicesDescription} />
                </>
              )}

              <Divider my="lg" />
              <Text size="xs" fw={600}>Signature of Authorized Representative:</Text>
              <Group mt="xl" gap="xl">
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 300, height: 30 }} />
                  <Text size="xs" c="dimmed">Signature</Text>
                </Stack>
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 150, height: 30 }} />
                  <Text size="xs" c="dimmed">Date</Text>
                </Stack>
              </Group>
            </Paper>
            </div>
          )}
        </Tabs.Panel>

        {/* ═══ CERTIFICATIONS ══════════════════════════════ */}
        <Tabs.Panel value="cert" pt="md">
          <Group mb="md" wrap="wrap">
            <Select size="sm" placeholder="Filter by disaster..." searchable clearable
              value={selectedDisaster} onChange={handleDisasterChange} data={disasterOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Filter by applicant..." searchable clearable
              value={selectedApplicant} onChange={handleApplicantChange} data={orgOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Select a project..." searchable clearable
              value={selectedProject} onChange={setSelectedProject} data={filteredProjectOptions}
              style={{ flex: 1, maxWidth: 500 }} />
            <Button size="sm" leftSection={<IconPrinter size={14} />} disabled={!project}
              onClick={() => printDiv(certRef.current)}>Print / PDF</Button>
          </Group>

          {!project ? (
            <Paper p="xl" withBorder ta="center">
              <Text c="dimmed">Select a project to generate its Applicant Certification.</Text>
            </Paper>
          ) : (
            <div ref={certRef}>
            <Paper p="lg" radius="md" withBorder>
              <div className="header">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Emergency Management Agency</Text>
                <Text size="lg" fw={700}>APPLICANT&apos;S CERTIFICATION</Text>
                <Text size="xs">Project {project.projectNumber} — {project.projectTitle}</Text>
              </div>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Project Identification
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Stack gap={2}>
                  <Field label="Disaster" value={`DR-${disaster?.disasterNumber || '—'}`} />
                  <Field label="Applicant" value={org?.orgName || '—'} />
                  <Field label="Project Number" value={project.projectNumber} />
                </Stack>
                <Stack gap={2}>
                  <Field label="Category" value={`${project.category} — ${DAMAGE_CATEGORY_LABELS[project.category as DamageCategory] || ''}`} />
                  <Field label="Estimated Cost" value={fmt(num(project.estimatedCost))} />
                  <Field label="FEMA Project ID" value={project.femaProjectId} />
                </Stack>
              </SimpleGrid>

              <Text size="sm" fw={700} c={primary[800]} mt="lg" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Certifications
              </Text>
              <Stack gap="md" mt="sm">
                {[
                  { label: '1. Legal Responsibility', desc: 'I certify that the applicant has the legal authority to apply for federal assistance and the institutional, managerial, and financial capability to ensure proper planning, management, and completion of the project.', field: project.certLegalResponsibility },
                  { label: '2. Insurance Compliance', desc: 'I certify that the applicant will comply with all applicable insurance requirements for federally assisted facilities, including the requirement to obtain and maintain insurance coverage as a condition of receiving federal disaster assistance.', field: project.certInsuranceCompliance },
                  { label: '3. Procurement Compliance', desc: 'I certify that the applicant will comply with applicable federal procurement standards under 2 CFR Part 200, including requirements for competition, cost/price analysis, and documentation of procurement activities.', field: project.certProcurementCompliance },
                  { label: '4. No Duplication of Work', desc: 'I certify that the work described in this project does not duplicate any other federal, state, or local assistance, and that the applicant will not seek or receive duplicate assistance for the same work.', field: project.certNoWorkDuplication },
                ].map(cert => (
                  <Paper key={cert.label} p="sm" radius="sm" style={{ background: '#f8f9fa' }}>
                    <Group gap="xs" mb={4}>
                      <Badge size="sm" color={cert.field ? 'var(--color-success)' : '#939598'} variant="light">
                        {cert.field || 'Pending'}
                      </Badge>
                      <Text size="xs" fw={700}>{cert.label}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">{cert.desc}</Text>
                  </Paper>
                ))}
              </Stack>

              <Divider my="lg" />
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Stack gap={2}>
                  <Field label="Certified By" value={project.certSignedBy || '(not yet signed)'} />
                  <div style={{ borderBottom: '1px solid #333', width: 300, height: 30, marginTop: 16 }} />
                  <Text size="xs" c="dimmed">Signature of Authorized Representative</Text>
                </Stack>
                <Stack gap={2}>
                  <Field label="Date" value={project.certSignedDate ? fmtDate(project.certSignedDate) : '(not yet signed)'} />
                  <div style={{ borderBottom: '1px solid #333', width: 200, height: 30, marginTop: 16 }} />
                  <Text size="xs" c="dimmed">Title</Text>
                </Stack>
              </SimpleGrid>
            </Paper>
            </div>
          )}
        </Tabs.Panel>
        {/* ═══ PNP FACILITY QUESTIONNAIRE (009-0-121) ════════ */}
        <Tabs.Panel value="pnp" pt="md">
          <Group mb="md">
            <Select size="sm" placeholder="Select an applicant..." searchable clearable
              value={selectedOrg} onChange={setSelectedOrg} data={orgOptions}
              style={{ flex: 1, maxWidth: 400 }} />
            <Button size="sm" leftSection={<IconPrinter size={14} />} disabled={!selectedOrg}
              onClick={() => printDiv(pnpRef.current)}>Print / PDF</Button>
          </Group>

          {!selectedOrg ? (
            <Paper p="xl" withBorder ta="center">
              <Text c="dimmed">Select an applicant to generate the PNP Facility Questionnaire.</Text>
            </Paper>
          ) : selectedOrgObj.orgType !== 'pnp' && selectedOrgObj.orgType !== 'nonprofit' ? (
            <Paper p="xl" withBorder ta="center">
              <Badge color="#C68700" mb="sm">Not a PNP</Badge>
              <Text c="dimmed">This form is only applicable to Private Non-Profit (PNP) organizations.</Text>
              <Text size="xs" c="dimmed" mt="xs">{selectedOrgObj.orgName} is classified as &quot;{selectedOrgObj.orgType}&quot;.</Text>
            </Paper>
          ) : (
            <div ref={pnpRef}>
            <Paper p="lg" radius="md" withBorder>
              <div className="header">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Emergency Management Agency</Text>
                <Text size="lg" fw={700}>PRIVATE NON-PROFIT (PNP) FACILITY QUESTIONNAIRE</Text>
                <Text size="xs" c="dimmed">FEMA Form 009-0-121</Text>
              </div>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section A — Applicant Information
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Stack gap={2}>
                  <Field label="Applicant Name" value={selectedOrgObj.orgName} />
                  <Field label="FEMA Applicant ID" value={selectedOrgObj.femaApplicantId} />
                  <Field label="UEI Number" value={selectedOrgObj.ueiNumber} />
                </Stack>
                <Stack gap={2}>
                  <Field label="Disaster Number" value={`DR-${disaster?.disasterNumber || '—'}`} />
                  <Field label="Contact" value={selectedOrgObj.primaryContact} />
                  <Field label="Phone" value={selectedOrgObj.primaryPhone} />
                </Stack>
              </SimpleGrid>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section B — Organization Classification
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Stack gap={2}>
                  <Field label="PNP Type / Category" value={selectedOrgObj.pnpType} />
                  <Field label="Tax Exempt Status Verified" value={selectedOrgObj.pnpTaxExemptVerified} />
                  <Field label="Tax Exempt Letter on File" value={selectedOrgObj.pnpTaxExemptLetter} />
                </Stack>
                <Stack gap={2}>
                  <Field label="EIN / Tax ID" value={selectedOrgObj.einTaxId} />
                  <Field label="SAM Registered" value={selectedOrgObj.samRegistered} />
                  <Field label="SAM Expiration" value={fmtDate(selectedOrgObj.samExpirationDate)} />
                </Stack>
              </SimpleGrid>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section C — Facility Information
              </Text>
              <Stack gap={4}>
                <Field label="Open to General Public" value={selectedOrgObj.pnpOpenToPublic} />
                <Field label="Critical Facility" value={selectedOrgObj.pnpCriticalFacility} />
                <Field label="Services Provided" value={selectedOrgObj.pnpServicesDescription} />
              </Stack>
              <Paper p="sm" radius="sm" mt="xs" style={{ background: '#f8f9fa' }}>
                <Text size="xs" c="dimmed" fs="italic">
                  A PNP facility must provide an essential service of a governmental nature to the general public
                  or be a facility that is open to the general public and provides a critical service (education, emergency,
                  medical, custodial care, utility, or other essential government-type service).
                </Text>
              </Paper>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Section D — Eligibility Determination
              </Text>
              <Stack gap={4}>
                <Text size="xs">
                  <strong>1.</strong> Is the organization a PNP as defined by 44 CFR 206.221(e)?{' '}
                  <Badge size="xs" color={selectedOrgObj.pnpTaxExemptVerified === 'Yes' ? 'var(--color-success)' : '#939598'} variant="light">
                    {selectedOrgObj.pnpTaxExemptVerified === 'Yes' ? 'YES' : 'PENDING'}
                  </Badge>
                </Text>
                <Text size="xs">
                  <strong>2.</strong> Does the facility provide services that are otherwise a responsibility of government?{' '}
                  <Badge size="xs" color={selectedOrgObj.pnpOpenToPublic === 'Yes' ? 'var(--color-success)' : '#939598'} variant="light">
                    {selectedOrgObj.pnpOpenToPublic === 'Yes' ? 'YES' : 'PENDING'}
                  </Badge>
                </Text>
                <Text size="xs">
                  <strong>3.</strong> Is the facility a critical service facility (education, emergency, medical, custodial care, utility)?{' '}
                  <Badge size="xs" color={selectedOrgObj.pnpCriticalFacility === 'Yes' ? 'var(--color-success)' : '#939598'} variant="light">
                    {selectedOrgObj.pnpCriticalFacility === 'Yes' ? 'YES' : 'PENDING'}
                  </Badge>
                </Text>
              </Stack>

              {/* Projects for this PNP */}
              {(() => {
                const pnpProjects = projects.filter(p => String(p.organizationId) === selectedOrg);
                return pnpProjects.length > 0 ? (
                  <>
                    <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                      Section E — Associated Projects
                    </Text>
                    <Table striped withTableBorder withColumnBorders>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Project #</Table.Th>
                          <Table.Th>Title</Table.Th>
                          <Table.Th>Category</Table.Th>
                          <Table.Th ta="right">Est. Cost</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {pnpProjects.map(p => (
                          <Table.Tr key={p.Rid}>
                            <Table.Td><Text size="xs">{p.projectNumber}</Text></Table.Td>
                            <Table.Td><Text size="xs">{p.projectTitle}</Text></Table.Td>
                            <Table.Td><Text size="xs">{p.category} — {DAMAGE_CATEGORY_LABELS[p.category as DamageCategory] || ''}</Text></Table.Td>
                            <Table.Td ta="right"><Text size="xs">{fmt(num(p.estimatedCost))}</Text></Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </>
                ) : null;
              })()}

              <Divider my="lg" />
              <Text size="xs" fw={600}>Certification:</Text>
              <Text size="xs" c="dimmed" mt="xs">
                I certify that the information provided above is true and correct. The facility described qualifies
                as a Private Non-Profit facility and provides services or functions of a governmental nature.
              </Text>
              <Group mt="xl" gap="xl">
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 300, height: 30 }} />
                  <Text size="xs" c="dimmed">Signature of Authorized Representative</Text>
                </Stack>
                <Stack gap={2}>
                  <div style={{ borderBottom: '1px solid #333', width: 150, height: 30 }} />
                  <Text size="xs" c="dimmed">Date</Text>
                </Stack>
              </Group>
            </Paper>
            </div>
          )}
        </Tabs.Panel>
        {/* ═══ PHOTO DOCUMENTATION SHEET (009-0-91D) ════════════ */}
        <Tabs.Panel value="photos" pt="md">
          <Group mb="md" wrap="wrap">
            <Select size="sm" placeholder="Filter by disaster..." searchable clearable
              value={selectedDisaster} onChange={handleDisasterChange} data={disasterOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Filter by applicant..." searchable clearable
              value={selectedApplicant} onChange={handleApplicantChange} data={orgOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Select a project..." searchable clearable
              value={selectedProject} onChange={setSelectedProject} data={filteredProjectOptions}
              style={{ flex: 1, maxWidth: 500 }} />
            <Button size="sm" leftSection={<IconPrinter size={14} />} disabled={!project}
              onClick={() => printDiv(photoRef.current)}>Print / PDF</Button>
          </Group>

          {!project ? (
            <Paper p="xl" withBorder ta="center">
              <Text c="dimmed">Select a project to generate its Photo Documentation Sheet.</Text>
            </Paper>
          ) : (
            <div ref={photoRef}>
            <Paper p="lg" radius="md" withBorder>
              <div className="header">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Emergency Management Agency</Text>
                <Text size="lg" fw={700}>PHOTO DOCUMENTATION SHEET</Text>
                <Text size="xs" c="dimmed">FEMA Form 009-0-91D</Text>
                <Group justify="center" gap="lg" mt={4}>
                  <Text size="xs">Disaster: DR-{disaster?.disasterNumber || '—'}</Text>
                  <Text size="xs">PA ID: {org?.femaApplicantId || '—'}</Text>
                  <Text size="xs">Project: {project.projectNumber}</Text>
                </Group>
              </div>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Project Information
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Stack gap={2}>
                  <Field label="Applicant" value={org?.orgName || '—'} />
                  <Field label="Project Title" value={project.projectTitle} />
                </Stack>
                <Stack gap={2}>
                  <Field label="Location" value={`${project.projectAddress}, ${project.projectCity}, ${project.projectState} ${project.projectZip}`} />
                  <Field label="Category" value={`${project.category} — ${DAMAGE_CATEGORY_LABELS[project.category as DamageCategory] || ''}`} />
                </Stack>
              </SimpleGrid>

              <Text size="sm" fw={700} c={primary[800]} mt="md" mb="xs" style={{ borderBottom: '2px solid var(--color-primary-800)', paddingBottom: 2 }}>
                Photographs
              </Text>
              {(() => {
                const projDocs = documents.filter(d =>
                  d.projectId === String(project.Rid) &&
                  (d.mimeType?.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp)$/i.test(d.documentName || ''))
                );
                if (projDocs.length === 0) {
                  return <Text size="xs" c="dimmed">No photos uploaded for this project.</Text>;
                }
                return (
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    {projDocs.map((doc, idx) => {
                      const thumbSrc = doc.notes?.startsWith('data:') ? doc.notes : '';
                      return (
                        <Paper key={doc.Rid} p="sm" withBorder>
                          <Text size="xs" fw={600} mb={4}>Photo {idx + 1}</Text>
                          <div style={{ background: '#f0f0f0', height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4, marginBottom: 8, overflow: 'hidden' }}>
                            {thumbSrc ? (
                              <img
                                src={thumbSrc}
                                alt={doc.documentName}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                              />
                            ) : (
                              <Text size="xs" c="dimmed">No preview available</Text>
                            )}
                          </div>
                          <Text size="xs"><strong>File:</strong> {doc.documentName}</Text>
                          <Text size="xs"><strong>Date:</strong> {fmtDateTime(doc.uploadDate)}</Text>
                          <Text size="xs"><strong>Description:</strong> {doc.description || '—'}</Text>
                          {doc.geotagLat && doc.geotagLng && (
                            <Text size="xs"><strong>GPS:</strong> {doc.geotagLat}, {doc.geotagLng}</Text>
                          )}
                        </Paper>
                      );
                    })}
                  </SimpleGrid>
                );
              })()}

              <Divider my="lg" />
              <Text size="xs" c="dimmed">
                Photos document the location, nature, and extent of damage to the facility described in the Project Worksheet.
                Pre-disaster photos should be included when available.
              </Text>
            </Paper>
            </div>
          )}
        </Tabs.Panel>

        {/* ═══ FEMA PACKAGE EXPORT ══════════════════════════════ */}
        <Tabs.Panel value="package" pt="md">
          <Group mb="md" wrap="wrap">
            <Select size="sm" placeholder="Filter by disaster..." searchable clearable
              value={selectedDisaster} onChange={handleDisasterChange} data={disasterOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Filter by applicant..." searchable clearable
              value={selectedApplicant} onChange={handleApplicantChange} data={orgOptions}
              style={{ minWidth: 200 }} />
            <Select size="sm" placeholder="Select a project..." searchable clearable
              value={selectedProject} onChange={setSelectedProject} data={filteredProjectOptions}
              style={{ flex: 1, maxWidth: 500 }} />
          </Group>

          {!project ? (
            <Paper p="xl" withBorder ta="center">
              <Text c="dimmed">Select a project to generate the FEMA submission package.</Text>
            </Paper>
          ) : (
            <Stack gap="md">
              <Paper p="lg" withBorder>
                <Text fw={600} mb="sm">FEMA Submission Package — {project.projectNumber}</Text>
                <Text size="sm" c="dimmed" mb="md">
                  Generate a complete set of official FEMA forms filled with project data.
                  Uses the actual government PDF templates with form fields populated from your data.
                </Text>

                <Stack gap="xs" mb="md">
                  <Text size="sm" fw={600}>Package Contents:</Text>
                  {([
                    ['009-0-91', 'Project Worksheet', true],
                    ['009-0-91A', 'Force Account Labor Summary', projLabor.length > 0],
                    ['009-0-91B', 'Force Account Equipment Summary', projEquip.length > 0],
                    ['009-0-91C', 'Materials Summary', projMaterials.length > 0],
                    ['009-0-91D', 'Photo Documentation Sheet', true],
                    ['009-0-120', 'Special Consideration Questions', !!comp],
                    ['009-0-126', 'Contract Work Summary', projContractCosts.length > 0],
                    ['009-0-49', 'Request for Public Assistance', !!org],
                  ] as [string, string, boolean][]).map(([form, label, ready]) => (
                    <Group key={form} gap="xs">
                      <Badge size="xs" color={ready ? 'var(--color-success)' : '#939598'} variant="filled" circle>
                        {ready ? '✓' : '—'}
                      </Badge>
                      <Text size="sm">{form} — {label}</Text>
                      {!ready && <Text size="xs" c="dimmed">(no data — will be skipped)</Text>}
                    </Group>
                  ))}
                </Stack>

                <Button
                  size="sm"
                  loading={packaging}
                  leftSection={<IconPrinter size={14} />}
                  onClick={async () => {
                    if (!project) return;
                    setPackaging(true);
                    try {
                      const { default: JSZip } = await import('jszip');
                      const zip = new JSZip();
                      const fmtC = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
                      const nowStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

                      function makePADoc(title: string, formNum: string): jsPDF {
                        const doc = new jsPDF();
                        const w = doc.internal.pageSize.getWidth();
                        // Header bar
                        doc.setFillColor(44, 1, 214);
                        doc.rect(0, 0, w, 22, 'F');
                        doc.setFontSize(7);
                        doc.setFont('helvetica', 'normal');
                        doc.setTextColor(255, 255, 255);
                        doc.text('U.S. DEPARTMENT OF HOMELAND SECURITY — FEDERAL EMERGENCY MANAGEMENT AGENCY', w / 2, 7, { align: 'center' });
                        doc.setFontSize(13);
                        doc.setFont('helvetica', 'bold');
                        doc.text(title.toUpperCase(), w / 2, 15, { align: 'center' });
                        doc.setFontSize(8);
                        doc.setFont('helvetica', 'normal');
                        doc.setTextColor(180, 180, 180);
                        doc.text(`OMB Form ${formNum}`, w / 2, 20, { align: 'center' });
                        doc.setTextColor(0, 0, 0);
                        return doc;
                      }

                      function paSection(doc: jsPDF, label: string, y: number): number {
                        doc.setFontSize(9);
                        doc.setFont('helvetica', 'bold');
                        doc.setTextColor(44, 1, 214);
                        doc.text(label.toUpperCase(), 14, y);
                        doc.setDrawColor(44, 1, 214);
                        doc.line(14, y + 2, doc.internal.pageSize.getWidth() - 14, y + 2);
                        doc.setTextColor(0, 0, 0);
                        return y + 10;
                      }

                      function addPAFooter(doc: jsPDF) {
                        const total = doc.getNumberOfPages();
                        for (let i = 1; i <= total; i++) {
                          doc.setPage(i);
                          const w = doc.internal.pageSize.getWidth();
                          const h = doc.internal.pageSize.getHeight();
                          doc.setFontSize(7);
                          doc.setFont('helvetica', 'normal');
                          doc.setTextColor(150, 150, 150);
                          doc.text(`Generated ${nowStr} — OpenGov EMC`, 14, h - 8);
                          doc.text(`Page ${i} of ${total}`, w - 14, h - 8, { align: 'right' });
                        }
                      }

                      function checkPAPage(doc: jsPDF, y: number, needed = 30): number {
                        if (y + needed > doc.internal.pageSize.getHeight() - 20) {
                          doc.addPage();
                          return 28;
                        }
                        return y;
                      }

                      // ── 1. Project Worksheet (009-0-91) ──────────────
                      {
                        const doc = makePADoc('Project Worksheet', '009-0-91');
                        let y = 30;

                        y = paSection(doc, 'Section A — Disaster & Project Identification', y);
                        autoTable(doc, {
                          startY: y,
                          theme: 'plain',
                          styles: { fontSize: 8, cellPadding: 2 },
                          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                          body: [
                            ['Disaster Number:', `DR-${disaster?.disasterNumber || '—'}`],
                            ['PA ID:', org?.femaApplicantId || '—'],
                            ['Project Number:', project.projectNumber],
                            ['Date Prepared:', nowStr],
                            ['Damage Category:', `${project.category} — ${DAMAGE_CATEGORY_LABELS[project.category as DamageCategory] || ''}`],
                          ],
                        });
                        y = (doc as any).lastAutoTable.finalY + 8;

                        y = paSection(doc, 'Section B — Applicant Information', y);
                        autoTable(doc, {
                          startY: y,
                          theme: 'plain',
                          styles: { fontSize: 8, cellPadding: 2 },
                          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                          body: [
                            ['Applicant Name:', org?.orgName || '—'],
                            ['Applicant Type:', org?.orgType || '—'],
                            ['FIPS Code:', org?.fipsCode || '—'],
                            ['County / Parish:', project.projectCounty || project.projectCity || '—'],
                            ['Contact:', org?.primaryContact || '—'],
                            ['Phone:', org?.primaryPhone || '—'],
                            ['Hazard Mitigation:', project.hazardMitigationIncluded || 'No'],
                            ['Insurance:', ins.length > 0 ? `Yes (${ins.length} polic${ins.length === 1 ? 'y' : 'ies'})` : 'No'],
                            ['Special Considerations:', comp ? 'Yes' : 'No'],
                          ],
                        });
                        y = (doc as any).lastAutoTable.finalY + 8;
                        y = checkPAPage(doc, y, 50);

                        y = paSection(doc, 'Section C — Damaged Facility', y);
                        autoTable(doc, {
                          startY: y,
                          theme: 'plain',
                          styles: { fontSize: 8, cellPadding: 2 },
                          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                          body: [
                            ['Facility Name:', project.projectTitle],
                            ['Location:', `${project.projectAddress || ''}, ${project.projectCity || ''}, ${project.projectState || ''} ${project.projectZip || ''}`],
                            ['Latitude:', project.projectLat || '—'],
                            ['Longitude:', project.projectLng || '—'],
                            ['Work Type:', project.workType || '—'],
                            ['Est. Completion:', project.completionPctEstimate ? `${project.completionPctEstimate}%` : '—'],
                          ],
                        });
                        y = (doc as any).lastAutoTable.finalY + 8;
                        y = checkPAPage(doc, y, 50);

                        y = paSection(doc, 'Section D — Damage Description & Dimensions', y);
                        doc.setFontSize(8);
                        doc.setFont('helvetica', 'normal');
                        const descLines = doc.splitTextToSize(project.damageDescription || 'See attached damage documentation.', 180);
                        doc.text(descLines, 14, y);
                        y += descLines.length * 4 + 8;
                        y = checkPAPage(doc, y, 50);

                        y = paSection(doc, 'Section E — Scope of Work', y);
                        const sowLines = doc.splitTextToSize(project.scopeOfWork || 'See project scope documentation.', 180);
                        doc.text(sowLines, 14, y);
                        y += sowLines.length * 4 + 8;
                        y = checkPAPage(doc, y, 60);

                        y = paSection(doc, 'Section F — Cost Estimate', y);
                        const costRows = costs.map((c, i) => [
                          String(i + 1),
                          c.description || '—',
                          `${c.quantity || ''} ${c.unit || ''}`.trim() || '—',
                          fmtC(num(c.unitCost)),
                          fmtC(num(c.totalCost)),
                        ]);
                        if (costRows.length === 0) costRows.push(['—', 'No cost items entered', '', '', '$0']);
                        autoTable(doc, {
                          startY: y,
                          theme: 'grid',
                          headStyles: { fillColor: [44, 1, 214], fontSize: 8 },
                          styles: { fontSize: 8, cellPadding: 2 },
                          head: [['#', 'Description / Narrative', 'Qty / Unit', 'Unit Price', 'Cost']],
                          body: costRows,
                          foot: [['', '', '', 'TOTAL ESTIMATED COST:', fmtC(costTotal)]],
                          footStyles: { fontStyle: 'bold', fillColor: [240, 240, 250] },
                        });

                        addPAFooter(doc);
                        zip.file('01-Project-Worksheet-009-0-91.pdf', doc.output('arraybuffer'));
                      }

                      // ── 2. Force Account Labor (009-0-91A) ─────────────
                      if (projLabor.length > 0) {
                        const doc = makePADoc('Force Account Labor Summary', '009-0-91A');
                        let y = 30;
                        y = paSection(doc, 'Project Identification', y);
                        autoTable(doc, {
                          startY: y, theme: 'plain',
                          styles: { fontSize: 8, cellPadding: 2 },
                          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                          body: [
                            ['Applicant:', org?.orgName || '—'],
                            ['Project Number:', project.projectNumber],
                            ['Disaster:', `DR-${disaster?.disasterNumber || '—'}`],
                            ['Date:', nowStr],
                          ],
                        });
                        y = (doc as any).lastAutoTable.finalY + 8;
                        y = paSection(doc, 'Labor Records', y);
                        autoTable(doc, {
                          startY: y, theme: 'grid',
                          headStyles: { fillColor: [44, 1, 214], fontSize: 8 },
                          styles: { fontSize: 8, cellPadding: 2 },
                          head: [['Employee', 'Activity', 'Date', 'Reg Hrs', 'OT Hrs', 'Total Cost']],
                          body: projLabor.map(r => [r.employeeName || '—', r.activityDescription || '—', fmtDate(r.workDate), String(r.hoursRegular), String(r.hoursOvertime), fmtC(r.totalCost)]),
                          foot: [['', '', 'TOTALS:', String(projLabor.reduce((s, r) => s + r.hoursRegular, 0)), String(projLabor.reduce((s, r) => s + r.hoursOvertime, 0)), fmtC(projLabor.reduce((s, r) => s + r.totalCost, 0))]],
                          footStyles: { fontStyle: 'bold', fillColor: [240, 240, 250] },
                        });
                        addPAFooter(doc);
                        zip.file('02-Force-Account-Labor-009-0-91A.pdf', doc.output('arraybuffer'));
                      }

                      // ── 3. Force Account Equipment (009-0-91B) ──────────
                      if (projEquip.length > 0) {
                        const doc = makePADoc('Force Account Equipment Summary', '009-0-91B');
                        let y = 30;
                        y = paSection(doc, 'Project Identification', y);
                        autoTable(doc, {
                          startY: y, theme: 'plain',
                          styles: { fontSize: 8, cellPadding: 2 },
                          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                          body: [
                            ['Applicant:', org?.orgName || '—'],
                            ['Project Number:', project.projectNumber],
                            ['Disaster:', `DR-${disaster?.disasterNumber || '—'}`],
                            ['Date:', nowStr],
                          ],
                        });
                        y = (doc as any).lastAutoTable.finalY + 8;
                        y = paSection(doc, 'Equipment Records', y);
                        autoTable(doc, {
                          startY: y, theme: 'grid',
                          headStyles: { fillColor: [44, 1, 214], fontSize: 8 },
                          styles: { fontSize: 8, cellPadding: 2 },
                          head: [['Equipment', 'Type/Size', 'Operator', 'Date', 'Hours', 'Rate/Hr', 'Total']],
                          body: projEquip.map(e => [e.equipmentName || '—', [e.equipmentType, e.size].filter(Boolean).join('/') || '—', e.operator || '—', fmtDate(e.workDate), String(e.hoursUsed), fmtC(e.ratePerHour), fmtC(e.totalCost)]),
                          foot: [['', '', '', 'TOTALS:', String(projEquip.reduce((s, e) => s + e.hoursUsed, 0)), '', fmtC(projEquip.reduce((s, e) => s + e.totalCost, 0))]],
                          footStyles: { fontStyle: 'bold', fillColor: [240, 240, 250] },
                        });
                        addPAFooter(doc);
                        zip.file('03-Force-Account-Equipment-009-0-91B.pdf', doc.output('arraybuffer'));
                      }

                      // ── 4. Materials Summary (009-0-91C) ────────────────
                      if (projMaterials.length > 0) {
                        const doc = makePADoc('Materials Summary', '009-0-91C');
                        let y = 30;
                        y = paSection(doc, 'Project Identification', y);
                        autoTable(doc, {
                          startY: y, theme: 'plain',
                          styles: { fontSize: 8, cellPadding: 2 },
                          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                          body: [
                            ['Applicant:', org?.orgName || '—'],
                            ['Project Number:', project.projectNumber],
                            ['Disaster:', `DR-${disaster?.disasterNumber || '—'}`],
                            ['Date:', nowStr],
                          ],
                        });
                        y = (doc as any).lastAutoTable.finalY + 8;
                        y = paSection(doc, 'Materials & Supplies', y);
                        autoTable(doc, {
                          startY: y, theme: 'grid',
                          headStyles: { fillColor: [44, 1, 214], fontSize: 8 },
                          styles: { fontSize: 8, cellPadding: 2 },
                          head: [['Description', 'Vendor', 'Invoice #', 'Date', 'Qty', 'Unit Cost', 'Total']],
                          body: projMaterials.map(m => [m.description || '—', m.vendor || '—', m.invoiceNumber || '—', fmtDate(m.purchaseDate), String(m.quantity), fmtC(m.unitCost), fmtC(m.totalCost)]),
                          foot: [['', '', '', '', '', 'TOTAL:', fmtC(projMaterials.reduce((s, m) => s + m.totalCost, 0))]],
                          footStyles: { fontStyle: 'bold', fillColor: [240, 240, 250] },
                        });
                        addPAFooter(doc);
                        zip.file('04-Materials-Summary-009-0-91C.pdf', doc.output('arraybuffer'));
                      }

                      // ── 5. Photo Documentation Sheet (009-0-91D) ─────
                      {
                        const doc = makePADoc('Photo Documentation Sheet', '009-0-91D');
                        let y = 30;

                        y = paSection(doc, 'Project Identification', y);
                        autoTable(doc, {
                          startY: y,
                          theme: 'plain',
                          styles: { fontSize: 8, cellPadding: 2 },
                          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                          body: [
                            ['Disaster Number:', `DR-${disaster?.disasterNumber || '—'}`],
                            ['Applicant:', org?.orgName || '—'],
                            ['Project Number:', project.projectNumber],
                            ['Category:', project.category],
                            ['Date:', nowStr],
                          ],
                        });
                        y = (doc as any).lastAutoTable.finalY + 10;

                        const projPhotos = documents.filter(d => d.projectId === String(project.Rid) && (d.mimeType || '').startsWith('image'));
                        if (projPhotos.length === 0) {
                          doc.setFontSize(9);
                          doc.setTextColor(100, 100, 100);
                          doc.text('No photos attached to this project.', 14, y);
                        } else {
                          projPhotos.slice(0, 8).forEach((d, i) => {
                            y = checkPAPage(doc, y, 30);
                            doc.setFontSize(9);
                            doc.setFont('helvetica', 'bold');
                            doc.setTextColor(0, 0, 0);
                            doc.text(`Photo ${i + 1}: ${d.documentName || 'Untitled'}`, 14, y);
                            y += 5;
                            doc.setFont('helvetica', 'normal');
                            doc.setFontSize(8);
                            doc.setTextColor(80, 80, 80);
                            doc.text(`Date: ${d.uploadDate ? fmtDate(d.uploadDate) : 'N/A'}  |  By: ${d.uploadedBy || 'N/A'}`, 14, y);
                            y += 4;
                            if (d.description) {
                              const dl = doc.splitTextToSize(d.description, 180);
                              doc.text(dl, 14, y);
                              y += dl.length * 4;
                            }
                            doc.setDrawColor(200, 200, 200);
                            doc.rect(14, y, 180, 40);
                            doc.setFontSize(7);
                            doc.setTextColor(150, 150, 150);
                            doc.text('[Photo attachment — see project documents]', 104, y + 22, { align: 'center' });
                            y += 50;
                          });
                        }

                        addPAFooter(doc);
                        zip.file('05-Photo-Sheet-009-0-91D.pdf', doc.output('arraybuffer'));
                      }

                      // ── 3. Special Consideration Questions (009-0-120) — only if comp data exists
                      if (comp) {
                        const doc = makePADoc('Special Consideration Questions', '009-0-120');
                        let y = 30;

                        y = paSection(doc, 'Project Identification', y);
                        autoTable(doc, {
                          startY: y,
                          theme: 'plain',
                          styles: { fontSize: 8, cellPadding: 2 },
                          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                          body: [
                            ['Applicant:', org?.orgName || '—'],
                            ['PA ID:', org?.femaApplicantId || '—'],
                            ['Project Number:', project.projectNumber],
                            ['Project Name:', project.projectTitle],
                            ['Location:', project.projectAddress || '—'],
                            ['Date:', nowStr],
                          ],
                        });
                        y = (doc as any).lastAutoTable.finalY + 8;

                        y = paSection(doc, 'Environmental & Historic Compliance', y);
                        const scqRows: [string, string, string][] = [
                          ['Flood Insurance Required?', comp.floodInsuranceRequired || '—', ''],
                          ['Located in Floodplain?', comp.inFloodplain || '—', ''],
                          ['Coastal Barrier Resource Area?', comp.coastalBarrierArea || '—', ''],
                          ['NEPA Required?', comp.nepaRequired || '—', ''],
                          ['ESA Section 7 Consultation?', comp.esaConsultationRequired || '—', ''],
                          ['Historic Property Involved?', comp.historicProperty || '—', ''],
                          ['Wetlands Impacted?', comp.wetlandsImpacted || '—', ''],
                          ['HAZMAT Present?', comp.hazmatPresent || '—', ''],
                          ['Coastal Zone Management?', comp.coastalZone || '—', ''],
                        ];
                        autoTable(doc, {
                          startY: y,
                          theme: 'grid',
                          headStyles: { fillColor: [44, 1, 214], fontSize: 8 },
                          styles: { fontSize: 8, cellPadding: 3 },
                          head: [['Question', 'Answer', 'Comments']],
                          body: scqRows,
                        });
                        y = (doc as any).lastAutoTable.finalY + 8;

                        if (comp.complianceNotes) {
                          y = checkPAPage(doc, y, 30);
                          y = paSection(doc, 'Additional Compliance Notes', y);
                          const nl = doc.splitTextToSize(comp.complianceNotes, 180);
                          doc.setFontSize(8);
                          doc.text(nl, 14, y);
                        }

                        addPAFooter(doc);
                        zip.file('06-Special-Considerations-009-0-120.pdf', doc.output('arraybuffer'));
                      }

                      // ── 4. Request for Public Assistance (009-0-49) ──
                      if (org?.orgName) {
                        const doc = makePADoc('Request for Public Assistance', '009-0-49');
                        let y = 30;

                        y = paSection(doc, 'Applicant Information', y);
                        autoTable(doc, {
                          startY: y,
                          theme: 'plain',
                          styles: { fontSize: 8, cellPadding: 2 },
                          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                          body: [
                            ['Applicant Name:', org.orgName],
                            ['Applicant Type:', org.orgType || '—'],
                            ['FEMA Applicant ID:', org.femaApplicantId || '—'],
                            ['UEI Number:', org.ueiNumber || '—'],
                            ['EIN / Tax ID:', org.einTaxId || '—'],
                            ['Date Submitted:', disaster?.declarationDate ? fmtDate(disaster.declarationDate) : nowStr],
                            ['Disaster Number:', `DR-${disaster?.disasterNumber || '—'}`],
                          ],
                        });
                        y = (doc as any).lastAutoTable.finalY + 8;
                        y = checkPAPage(doc, y, 50);

                        y = paSection(doc, 'Physical Address', y);
                        autoTable(doc, {
                          startY: y,
                          theme: 'plain',
                          styles: { fontSize: 8, cellPadding: 2 },
                          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                          body: [
                            ['Street:', org.physicalAddress || '—'],
                            ['City:', org.physicalCity || '—'],
                            ['County / Parish:', org.physicalCounty || '—'],
                            ['State:', org.physicalState || '—'],
                            ['ZIP:', org.physicalZip || '—'],
                          ],
                        });
                        y = (doc as any).lastAutoTable.finalY + 8;
                        y = checkPAPage(doc, y, 50);

                        y = paSection(doc, 'Primary Contact', y);
                        autoTable(doc, {
                          startY: y,
                          theme: 'plain',
                          styles: { fontSize: 8, cellPadding: 2 },
                          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                          body: [
                            ['Name:', org.primaryContact || '—'],
                            ['Title:', org.primaryTitle || '—'],
                            ['Phone:', org.primaryPhone || '—'],
                            ['Email:', org.primaryEmail || '—'],
                          ],
                        });
                        y = (doc as any).lastAutoTable.finalY + 8;

                        if (org.altContact) {
                          y = checkPAPage(doc, y, 40);
                          y = paSection(doc, 'Alternate Contact', y);
                          autoTable(doc, {
                            startY: y,
                            theme: 'plain',
                            styles: { fontSize: 8, cellPadding: 2 },
                            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                            body: [
                              ['Name:', org.altContact],
                              ['Title:', org.altTitle || '—'],
                              ['Phone:', org.altPhone || '—'],
                              ['Email:', org.altEmail || '—'],
                            ],
                          });
                          y = (doc as any).lastAutoTable.finalY + 8;
                        }

                        const orgType = (org.orgType || '').toLowerCase();
                        if (orgType === 'pnp' || orgType === 'nonprofit') {
                          y = checkPAPage(doc, y, 30);
                          y = paSection(doc, 'Private Non-Profit Information', y);
                          autoTable(doc, {
                            startY: y,
                            theme: 'plain',
                            styles: { fontSize: 8, cellPadding: 2 },
                            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                            body: [
                              ['PNP Facility Type:', org.pnpType || '—'],
                              ['Tax-Exempt Verified:', org.pnpTaxExemptVerified || '—'],
                            ],
                          });
                          y = (doc as any).lastAutoTable.finalY + 8;
                        }

                        // SAM registration
                        y = checkPAPage(doc, y, 30);
                        y = paSection(doc, 'SAM.gov Registration', y);
                        autoTable(doc, {
                          startY: y,
                          theme: 'plain',
                          styles: { fontSize: 8, cellPadding: 2 },
                          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                          body: [
                            ['SAM Registered:', org.samRegistered || '—'],
                            ['SAM Expiration:', fmtDate(org.samExpirationDate)],
                            ['Congressional District:', org.congressionalDistrict || '—'],
                          ],
                        });

                        addPAFooter(doc);
                        zip.file('07-Request-for-PA-009-0-49.pdf', doc.output('arraybuffer'));
                      }

                      // ── 5. Contract Work Summary (009-0-126) ─────────
                      {
                        const doc = makePADoc('Contract Work Summary', '009-0-126');
                        let y = 30;

                        y = paSection(doc, 'Project Information', y);
                        autoTable(doc, {
                          startY: y,
                          theme: 'plain',
                          styles: { fontSize: 8, cellPadding: 2 },
                          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                          body: [
                            ['Date:', nowStr],
                            ['PA ID:', org?.femaApplicantId || '—'],
                            ['Project Number:', project.projectNumber],
                            ['Disaster Number:', `DR-${disaster?.disasterNumber || '—'}`],
                            ['Location:', `${project.projectAddress || ''}, ${project.projectCity || ''}`],
                            ['Category:', project.category],
                          ],
                        });
                        y = (doc as any).lastAutoTable.finalY + 8;

                        y = paSection(doc, 'Description of Work', y);
                        const sowL = doc.splitTextToSize(project.scopeOfWork || project.projectTitle, 180);
                        doc.setFontSize(8);
                        doc.text(sowL, 14, y);
                        y += sowL.length * 4 + 8;
                        y = checkPAPage(doc, y, 60);

                        y = paSection(doc, 'Contract Cost Items', y);
                        const contractCosts = costs.filter(c => c.costType === 'contracts');
                        const cwRows = contractCosts.map(c => [
                          c.sourceReference || c.description || '—',
                          c.description || '—',
                          fmtC(num(c.totalCost)),
                          c.notes || '',
                        ]);
                        if (cwRows.length === 0) cwRows.push(['No contract costs entered', '', '$0', '']);
                        const contractTotal = contractCosts.reduce((s, c) => s + num(c.totalCost), 0);
                        autoTable(doc, {
                          startY: y,
                          theme: 'grid',
                          headStyles: { fillColor: [44, 1, 214], fontSize: 8 },
                          styles: { fontSize: 8, cellPadding: 2 },
                          head: [['Contractor / Source', 'Invoice / Description', 'Amount', 'Comments']],
                          body: cwRows,
                          foot: [['', 'GRAND TOTAL:', fmtC(contractTotal), '']],
                          footStyles: { fontStyle: 'bold', fillColor: [240, 240, 250] },
                        });
                        y = (doc as any).lastAutoTable.finalY + 12;

                        // Certification signature block
                        y = checkPAPage(doc, y, 40);
                        doc.setFontSize(8);
                        doc.setFont('helvetica', 'italic');
                        doc.setTextColor(60, 60, 60);
                        const certText = 'I certify that to the best of my knowledge and belief the above information is correct and that all expenditures were made in accordance with applicable laws and regulations and the terms and conditions of the FEMA grant.';
                        const certL = doc.splitTextToSize(certText, 180);
                        doc.text(certL, 14, y);
                        y += certL.length * 4 + 12;
                        doc.setFont('helvetica', 'normal');
                        doc.setTextColor(0, 0, 0);
                        doc.line(14, y, 100, y);
                        doc.text(`Authorized Representative: ${org.primaryContact || '____________________________'}`, 14, y + 5);
                        doc.line(110, y, 196, y);
                        doc.text('Date', 110, y + 5);
                        y += 14;
                        doc.line(14, y, 100, y);
                        doc.text(`Prepared By: ${project.assignedSpecialist || '____________________________'}`, 14, y + 5);

                        addPAFooter(doc);
                        zip.file('08-Contract-Work-Summary-009-0-126.pdf', doc.output('arraybuffer'));
                      }

                      // Generate ZIP and download
                      const blob = await zip.generateAsync({ type: 'blob' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `FEMA-Package-${project.projectNumber}-${new Date().toISOString().split('T')[0]}.zip`;
                      a.click();
                      URL.revokeObjectURL(url);
                      notifications.show({ message: `FEMA package downloaded — ${Object.keys(zip.files).length} filled PDFs`, color: 'var(--color-success)' });
                    } catch (err) {
                      notifications.show({ message: `Package failed: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'var(--color-error)' });
                    } finally { setPackaging(false); }
                  }}
                >
                  Download FEMA Package (ZIP)
                </Button>
              </Paper>
            </Stack>
          )}
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
